/* eslint-disable */
import axios from 'axios';

// Store CSRF token in memory (fallback when cookies are blocked or unavailable)
// Primary mechanism: axios reads XSRF-TOKEN cookie automatically
// Fallback: memory cache used when cookie doesn't exist (e.g., localhost restrictions)
let csrfTokenCache: string | null = null;

// Function to set CSRF token in memory (for cases where cookies are blocked)
export const setCsrfToken = (token: string) => {
  csrfTokenCache = token;
  // Note: Cookie setting is handled by backend. Frontend only reads the cookie.
  // Memory cache serves as fallback when cookies are blocked (e.g., localhost)
};

// Function to clear CSRF token from memory (used during logout)
export const clearCsrfToken = () => {
  csrfTokenCache = null;
  // Note: Cookie clearing is handled by backend logout endpoint.
  // Frontend only clears in-memory cache to prevent stale token usage.
};

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true, // Required to send/receive cookies
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'x-xsrf-token',
});

// Endpoints that don't require CSRF protection
// Only /csrf-token itself is exempt (chicken-and-egg: can't have token before fetching it)
const CSRF_EXEMPT_ENDPOINTS = ['/csrf-token'];

// Request interceptor to add authorization token and handle CSRF exemptions
apiClient.interceptors.request.use(
  (config) => {
    // Add authorization token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.authorization = token;
    }

    // Check if endpoint is exempt from CSRF protection (exact match or starts with)
    const url = config.url || '';
    const isExempt = CSRF_EXEMPT_ENDPOINTS.some((endpoint) => {
      // Exact match or starts with endpoint followed by '?' (query params) or end of string
      return url === endpoint || url.startsWith(`${endpoint}?`);
    });

    if (isExempt) {
      // Disable axios built-in CSRF for this request
      config.xsrfCookieName = undefined;
      config.xsrfHeaderName = undefined;
    } else if (csrfTokenCache && !document.cookie.includes('XSRF-TOKEN')) {
      // Fallback: If cookie doesn't exist but we have token in memory, use it
      config.headers['x-xsrf-token'] = csrfTokenCache;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle CSRF token errors
    if (error.response?.status === 403) {
      const rawMessage = error.response?.data?.message;
      const csrfError = typeof rawMessage === 'string' ? rawMessage.toLowerCase() : '';
      // Be specific to CSRF-related phrases to avoid catching unrelated token errors
      if (csrfError.includes('csrf token') || csrfError.includes('xsrf token')) {
        console.error('CSRF token error. Please refresh the page.');
        // Optionally redirect to login or show error toast
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
